/**
 * Maximum size for a single uploaded file (3GB), enforced per file.
 *
 * Applies to regular uploads and to each individual file extracted from a ZIP
 * archive or email attachment on the worker. The ZIP/email archives
 * themselves are exempt — they are unpacked server-side and every contained
 * file is checked against this limit instead.
 *
 * Shared with the frontend dropzone (client-side `react-dropzone` validation)
 * so both ends enforce exactly the same number.
 */
export const MAX_UPLOAD_FILE_SIZE_BYTES = 3 * 1024 * 1024 * 1024; // 3GB

/**
 * Maximum size for a single uploaded ZIP archive (120GB), enforced by the frontend dropzone.
 */
export const MAX_UPLOAD_ZIP_SIZE_BYTES = 120 * 1024 * 1024 * 1024; // 120GB

/**
 * Maximum number of entries permitted inside a single uploaded ZIP archive.
 */
export const MAX_UPLOAD_ZIP_ENTRY_COUNT = 500_000;

/**
 * Maximum size for a single uploaded PST (Outlook mail archive).
 */
export const MAX_UPLOAD_PST_SIZE_BYTES = 10 * 1024 * 1024 * 1024; // 10GB

/**
 * Maximum number of items (across all folders) in a single uploaded PST.
 */
export const MAX_PST_MESSAGE_COUNT = 50_000;

/**
 * Maximum decompressed size of a DEFLATE (compressed) nested archive (ZIP) that must be inflated on
 * the worker. Above this, one shard couldn't inflate and re-upload it inside the extraction
 * timeout, so it's rejected at plan time.
 */
export const MAX_INFLATE_ARCHIVE_BYTES = 20 * 1024 * 1024 * 1024; // 20GB

/**
 * Maximum number of files accepted in a single upload batch.
 *
 * Enforced by the frontend dropzone (`react-dropzone`'s `maxFiles`), which
 * rejects the whole batch with a `too-many-files` error when exceeded. This is
 * a per-drop limit, not a per-matter total.
 */
export const MAX_UPLOAD_FILE_COUNT = 500_000;

/**
 * Size at or above which a file extracted from a ZIP is streamed to disk instead
 * of buffered in memory on the worker.
 *
 * This is a memory-management threshold for upload processing, not a rejection
 * limit — entries above it are still processed, just streamed.
 */
export const STREAMING_THRESHOLD_BYTES = 50 * 1024 * 1024; // 50MB

/**
 * Maximum PDF size the encryption detector can load into PDFium.
 *
 * NOT a business upload limit — this is a hard technical ceiling of the PDFium
 * WebAssembly runtime, whose heap is 2 GiB. PDFs at or above this can't be
 * loaded to verify encryption, so any such PDF the tail prefilter couldn't clear
 * is rejected as encrypted rather than passed through unverified. Do not raise
 * this to match the upload limit — it is bounded by the WASM heap, not policy.
 */
export const MAX_PDFIUM_BYTES = 2 * 1024 * 1024 * 1024; // 2 GiB

/**
 * Memory budget for the PDF-conversion worker container.
 *
 * NOT a business upload limit — this reflects the worker's provisioned container
 * memory and is used to bound conversion work accordingly. It happens to share
 * the 2GB value with {@link MAX_PDFIUM_BYTES} but is an unrelated constraint;
 * keep them separate.
 */
export const PDF_CONVERSION_CONTAINER_MEMORY_BYTES = 2 * 1024 * 1024 * 1024; // 2GB
